import { useCallback, useEffect, useState } from 'react';
import { Button } from '@auctisync/ui';
import { dollarsToCents } from '@auctisync/shared';
import {
  DEFAULT_ALERT_WINDOW_MINUTES,
  NO_ALERTS,
  fetchLotAlerts,
  saveLotAlerts,
} from '@auctisync/api-client';
import { supabase } from '../lib/supabase';
import { useSession } from '../hooks/useSession';
import { useToast } from './Toast';

interface LotAlertControlsProps {
  lotId: string;
  /** Whether this lot has a reserve (drives whether the reserve-met toggle is shown). */
  hasReserve: boolean;
  /** Whether the reserve is currently met (drives whether the toggle is offered or already silent). */
  reserveMet: boolean | null;
}

interface ExistingAlerts {
  priceBelowThresholdCents: number | null;
  priceBelowWindowMinutes: number | null;
  reserveMetActive: boolean;
}

/**
 * Module #23 chunk 8 — bidder smart-alert controls for a single lot.
 *
 * v1 rule types:
 *   - price_below: BARGAIN alert — notify once when the lot enters its final
 *     window (bidder-chosen 15/30/60 min, stored in rule_params.window_minutes,
 *     default 30) and the current bid is still below the threshold (evaluated by
 *     the notify_bargain_alerts pg_cron, not on every bid). The price only rises
 *     in an ascending auction, so this is a "still a deal near close" alert.
 *   - reserve_met: notify when the lot's reserve_met flips to true
 *
 * Both alerts are per-lot. Cross-tenant rule types (new_in_category, etc.)
 * are deferred. Dashboard management UI is also deferred — for v1 bidders
 * set alerts here on the lot they care about; an "alerts management" page
 * follows once usage data shows it's needed.
 */
export function LotAlertControls({ lotId, hasReserve, reserveMet }: LotAlertControlsProps) {
  const { session } = useSession();
  const toast = useToast();
  const userId = session?.user?.id ?? null;
  const [existing, setExisting] = useState<ExistingAlerts | null>(null);
  const [thresholdDollars, setThresholdDollars] = useState('');
  // Bidder-chosen "near closing" window in minutes (15/30/60; default 30 to
  // match the platform's existing "closing soon" notification window).
  const [windowMinutes, setWindowMinutes] = useState(30);
  const [reserveMetEnabled, setReserveMetEnabled] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reading and saving go through @auctisync/api-client so the native app runs
  // the identical rules — in particular the re-arm guard in `saveLotAlerts`,
  // which is invisible in the schema and whose failure mode is notifying a
  // bidder twice about the same lot.
  const load = useCallback(async () => {
    if (!userId) {
      setExisting(null);
      return;
    }
    const result = await fetchLotAlerts(supabase, { userId, lotId });
    if (!result.ok) {
      setError(result.message);
      return;
    }
    const { alerts } = result;
    if (alerts.priceBelowThresholdCents !== null) {
      setThresholdDollars((alerts.priceBelowThresholdCents / 100).toString());
    }
    setWindowMinutes(alerts.priceBelowWindowMinutes ?? DEFAULT_ALERT_WINDOW_MINUTES);
    // Reflect the TRUE stored reserve state, including OFF — otherwise
    // unchecking + saving leaves the toggle stale-checked and a later save
    // resurrects the just-deleted alert.
    setReserveMetEnabled(alerts.reserveMetActive);
    setExisting(alerts);
  }, [userId, lotId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!session) {
    return (
      <div style={infoBlockStyle}>
        <a href="/my" style={{ color: 'var(--brand-accent)' }}>
          Sign in
        </a>{' '}
        to set price alerts on this lot.
      </div>
    );
  }
  if (!userId || existing === null) return null;

  async function save() {
    if (!userId) return;
    // Snapshot for the type checker; the early return above already guarantees
    // `existing` is non-null here.
    const prior = existing ?? NO_ALERTS;
    setPending(true);
    setError(null);

    const trimmed = thresholdDollars.trim();
    const parsed = trimmed === '' ? null : Number(trimmed);
    if (parsed !== null && !Number.isFinite(parsed)) {
      setError('Enter an amount like 25 or 25.50.');
      setPending(false);
      return;
    }

    // Every rule — including the re-arm guard that stops an already-fired
    // bargain alert being resurrected by an incidental save — lives in
    // `saveLotAlerts`. It used to be written out here, which is why the native
    // app had no alerts at all: there was nothing to share.
    const result = await saveLotAlerts(supabase, {
      userId,
      lotId,
      hasReserve,
      thresholdCents: parsed === null ? null : dollarsToCents(parsed),
      windowMinutes,
      reserveMetEnabled,
      existing: prior,
    });

    if (result.ok) {
      await load();
      toast.success('Alerts saved.');
    } else {
      setError(result.message);
    }
    setPending(false);
  }

  return (
    <div style={infoBlockStyle}>
      <div
        style={{
          fontSize: '0.85rem',
          fontWeight: 600,
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          marginBottom: '0.5rem',
        }}
      >
        Smart alerts
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.85rem', color: 'var(--text)' }}>
            Notify me once if the current bid is still below
          </span>
          <input
            type="number"
            min="0"
            step="1"
            inputMode="decimal"
            placeholder="$ amount (e.g. 50)"
            value={thresholdDollars}
            onChange={(e) => setThresholdDollars(e.target.value)}
            style={inputStyle}
          />
        </label>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.4rem',
            fontSize: '0.85rem',
            color: 'var(--text)',
          }}
        >
          when the lot has
          <select
            value={windowMinutes}
            onChange={(e) => setWindowMinutes(Number(e.target.value))}
            aria-label="How close to closing to send the alert"
            style={selectStyle}
          >
            <option value={15}>15 minutes</option>
            <option value={30}>30 minutes</option>
            <option value={60}>60 minutes</option>
          </select>
          left
        </label>
        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
          A single heads-up when this lot enters its final {windowMinutes} minutes — your cue to
          jump in before it closes.
        </span>
      </div>
      {hasReserve && reserveMet !== true ? (
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            marginTop: '0.5rem',
            fontSize: '0.85rem',
            color: 'var(--text)',
          }}
        >
          <input
            type="checkbox"
            checked={reserveMetEnabled}
            onChange={(e) => setReserveMetEnabled(e.target.checked)}
          />
          Notify me when the reserve is met
        </label>
      ) : null}
      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', alignItems: 'center' }}>
        <Button
          type="button"
          size="sm"
          onClick={() => {
            void save();
          }}
          disabled={pending}
        >
          {pending ? 'Saving…' : 'Save alerts'}
        </Button>
      </div>
      {error ? (
        <p
          style={{ color: 'var(--text-danger, #991b1b)', fontSize: '0.85rem', marginTop: '0.5rem' }}
        >
          {error}
        </p>
      ) : null}
    </div>
  );
}

const infoBlockStyle: React.CSSProperties = {
  marginTop: '0.75rem',
  padding: '0.75rem 0.85rem',
  background: 'var(--surface-muted)',
  borderRadius: '0.5rem',
  fontSize: '0.85rem',
  color: 'var(--text)',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.4rem 0.6rem',
  border: '1px solid var(--border)',
  borderRadius: '0.375rem',
  background: 'var(--surface)',
  color: 'var(--text)',
  fontSize: '0.9rem',
};

const selectStyle: React.CSSProperties = {
  padding: '0.3rem 0.5rem',
  border: '1px solid var(--border)',
  borderRadius: 'var(--r-control, 0.375rem)',
  background: 'var(--surface)',
  color: 'var(--text)',
  fontSize: '0.85rem',
};
